import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import { flowRight as compose } from 'lodash';
import { FaBars, FaChevronDown } from 'react-icons/fa';
import { toastr } from 'react-redux-toastr';

import s from './HeaderRevamp.css';
import history from '../../history';
import Drawer from './Drawer';

// Component
import LoginModal from '../Shop/LoginModal/LoginModal';
import ForgotPasswordModal from '../Shop/ForgotPassword/ForgotPasswordModal';
import Link from '../Link/Link';
import HeaderModal from '../HeaderModal/HeaderModal';
import OrderModal from '../Shop/OrderModal/OrderModal';

// Action
import { openHeaderModal } from '../../actions/siteadmin/modalActions';
import {
  openOrderModal,
  closeOrderModal,
} from '../../actions/modalActions';
import { getPendingOrders } from '../../actions/shop/orders/getPendingOrders';

// helper
import { formatLocale } from '../../helpers/formatLocale';

// Local
import messages from '../../locale/messages';
import languageIcon from '../../../public/Icons/Language-black.svg';
import arrow from '../../../public/Icons/right.svg';

// Homepage sections (anchors set on <section id="..."> in routes/home/sections)
const SECTION_LINKS = [
  // { id: 'home', label: <FormattedMessage {...messages.home} /> },
  { id: 'about', label: <FormattedMessage {...messages.about} /> },
  { id: 'services', label: <FormattedMessage {...messages.homeServicesNav} /> },
  { id: 'service-area', label: <FormattedMessage {...messages.homeAreaNav} /> },
  { id: 'faq', label: <FormattedMessage {...messages.faq} /> },
  { id: 'news', label: 'News' },
  { id: 'footer-section', label: <FormattedMessage {...messages.contact} /> },
];

function Header(props) {
  const {
    siteName,
    socket,
    userId,
    currentLocale,
    openHeaderModal,
    openOrderModal,
    closeOrderModal,
    getPendingOrders,
  } = props;

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [pathname, setPathname] = useState(
    typeof window !== 'undefined' ? history.location.pathname : '/'
  );

  // Track active route to highlight the current nav item
  useEffect(() => {
    const unlisten = history.listen((location) => setPathname(location.pathname));
    return unlisten;
  }, []);

  // Scroll spy: highlight the section currently in view (homepage only)
  useEffect(() => {
    if (pathname !== '/' || typeof window === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    );

    SECTION_LINKS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [pathname]);

  // Socket listeners (replaces componentDidMount / render-time bindings)
  useEffect(() => {
    if (!socket || !userId) return undefined;

    const handleSocketResponse = async () => {
      await getPendingOrders();
      await openOrderModal();
    };

    socket.on(`orderReceivedShop-${userId}`, () => {
      handleSocketResponse();
    });

    socket.on(`bookingCancelled-${userId}`, (data) => {
      closeOrderModal();
      if (data && data.orderId) {
        toastr.warning(
          'Order Cancelled',
          `Order #${data.orderId} cancelled by customer, reason: ${data.cancelReason || 'Customer cancelled'}`,
          { timeOut: 60000 }
        );
      }
    });

    socket.on(`webAdminUserLogout-${userId}`, () => {
      if (typeof window !== 'undefined') window.location.assign('/adminUserLogout');
    });

    socket.on(`webUserLogout-${userId}`, () => {
      if (typeof window !== 'undefined') window.location.assign('/store');
    });

    return () => {
      socket.removeListener(`webUserLogout-${userId}`);
      socket.removeListener(`webAdminUserLogout-${userId}`);
      socket.removeListener(`orderReceivedShop-${userId}`);
      socket.removeListener(`bookingCancelled-${userId}`);
    };
  }, [socket, userId]);

  const openMenu = () => setMenuOpen(true);
  const closeMenu = () => setMenuOpen(false);

  const handleLanguage = () => {
    openHeaderModal('languageModal');
  };

  // Smooth-scroll to a homepage section; navigate home first when on another route
  const scrollToSection = (e, id) => {
    if (e) e.preventDefault();
    closeMenu();
    setActiveSection(id);

    const doScroll = () => {
      const el = typeof document !== 'undefined' && document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    if (history.location.pathname !== '/') {
      history.push('/');
      setTimeout(doScroll, 350);
    } else {
      doScroll();
    }
  };

  return (
    <header className={cx(s.navBg, 'mainMenu')}>
      <OrderModal />
      <LoginModal />
      <ForgotPasswordModal />
      <HeaderModal />

      <div className={s.container}>
        {/* Brand */}
        <Link className={s.brand} to="/">
          <img src="/images/ASAP-logo.svg" alt={siteName} />
        </Link>

        {/* Mobile toggle */}
        <button type="button" className={cx('menuToggle', s.menuToggle)} onClick={openMenu}>
          <FaBars />
        </button>

        {/* Desktop: centre nav + right actions */}
        <div className={s.menu}>
          <nav className={s.navigationLink}>
            {SECTION_LINKS.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => scrollToSection(e, item.id)}
                className={cx(s.navItem, {
                  [s.active]: pathname === '/' && activeSection === item.id,
                })}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className={s.actions}>
            <Link noLink onClick={handleLanguage} className={s.langSelector}>
              <img src={languageIcon} alt="language" />
              {/* <span>{formatLocale(currentLocale)}</span> */}
              <FaChevronDown className={s.caret} />
            </Link>


            <a href="https://linktr.ee/DOWNLOADASAP" className={cx(s.ctaBtnFilled, 'storeLinkRTL')} style={{ textDecoration: 'none' }}>
              Order Now
            </a>
            <a href="/store/signup" className={s.partnerBtn} style={{ textDecoration: 'none' }}>
              Become a Partner 🤝
            </a>
          </div>
        </div>

        {/* Mobile: Drawer */}
        <Drawer
          position="bottom"
          open={menuOpen}
          onClose={closeMenu}
          icon={
            <img src="/images/ASAP-logo.svg" alt={siteName} style={{ height: '24px', width: 'auto' }} />
          }
        >
          <nav className={s.navigationLink}>
            {SECTION_LINKS.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => scrollToSection(e, item.id)}
                className={cx(s.navItem, {
                  [s.active]: pathname === '/' && activeSection === item.id,
                })}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className={s.drawerActions}>
            <a href="https://linktr.ee/DOWNLOADASAP" className={s.drawerBtnPrimary}>
              Order Now
            </a>
            <a href="/store/signup" className={s.drawerBtnGhost}>
              Become a Partner 🤝
            </a>
          </div>
        </Drawer>
      </div>
    </header>
  );
}

const mapState = (state) => ({
  logo: state.siteSettings.data.homeLogo,
  logoHeight: state.siteSettings.data.logoHeight,
  logoWidth: state.siteSettings.data.logoWidth,
  siteName: state.siteSettings.data.siteName,
  userId: state.account && state.account.data && state.account.data.id,
  currentLocale: state.intl.locale,
});

const mapDispatch = {
  openHeaderModal,
  openOrderModal,
  closeOrderModal,
  getPendingOrders,
};

export default compose(
  withStyles(s),
  connect(mapState, mapDispatch)
)(Header);
